import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { ArrowRight, Star, ShoppingBag, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { shopifyFetch } from '@/shopify';
import { useCartStore, useWishlistStore } from '@/store';
import { toast } from 'sonner';

// FIX 7: Cache de ratings fora do componente — persiste durante toda a sessão
const ratingsCache = {};

async function fetchProductReviewStats(numericId, handle) {
  const cacheKey = numericId;

  // Retorna do cache se já foi buscado
  if (ratingsCache[cacheKey] !== undefined) {
    return ratingsCache[cacheKey];
  }

  try {
    const lookupRes = await fetch(
      `/api/judgeme-product?action=lookup&external_id=${numericId}&handle=${handle}`
    );

    const lookupData = await lookupRes.json();

    if (!lookupData.ok || !lookupData.product?.id) {
      ratingsCache[cacheKey] = null;
      return null;
    }

    const internalId = lookupData.product.id;

    const reviewsRes = await fetch(
      `/api/judgeme-product?action=reviews&internal_id=${internalId}&page=1&per_page=100`
    );

    const reviewsData = await reviewsRes.json();

    if (!reviewsData.ok || !Array.isArray(reviewsData.reviews)) {
      ratingsCache[cacheKey] = null;
      return null;
    }

    const firstPageReviews = reviewsData.reviews;
    const PER_PAGE = 100;
    let count = firstPageReviews.length;

    if (firstPageReviews.length === PER_PAGE) {
      let page = 2;
      while (page <= 20) {
        const moreRes = await fetch(
          `/api/judgeme-product?action=reviews&internal_id=${internalId}&page=${page}&per_page=${PER_PAGE}`
        );
        const moreData = await moreRes.json();
        if (!moreData.ok || !Array.isArray(moreData.reviews) || moreData.reviews.length === 0) break;
        count += moreData.reviews.length;
        if (moreData.reviews.length < PER_PAGE) break;
        page++;
      }
    }

    const average =
      firstPageReviews.length > 0
        ? firstPageReviews.reduce((sum, r) => sum + Number(r.rating || 0), 0) / firstPageReviews.length
        : null;

    const result = count === 0 ? { average: null, count: 0 } : { average, count };

    ratingsCache[cacheKey] = result;
    return result;
  } catch {
    ratingsCache[cacheKey] = null;
    return null;
  }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.18, delayChildren: 0.1 }
  }
};

const wordVariants = {
  hidden: { y: 80, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const { addItem } = useCartStore();
  const { toggleItem, isInWishlist } = useWishlistStore();

  const auraEntrance = useAnimation();
  const auraShimmer  = useAnimation();

  useEffect(() => {
    auraEntrance.start({
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 60, damping: 12, delay: 0.46 }
    });
    auraShimmer.start({
      backgroundPosition: ['200% 50%', '-200% 50%'],
      transition: { duration: 3.5, ease: 'linear', repeat: Infinity, delay: 0.46 }
    });
  }, [auraEntrance, auraShimmer]);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const data = await shopifyFetch(`
          query {
            products(first: 6) {
              edges {
                node {
                  id
                  title
                  handle
                  productType
                  availableForSale
                  featuredImage {
                    url
                    altText
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                    }
                    maxVariantPrice {
                      amount
                    }
                  }
                  variants(first: 50) {
                    edges {
                      node {
                        id
                        title
                        availableForSale
                        price {
                          amount
                        }
                        compareAtPrice {
                          amount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `);

        const edges = data.products.edges || [];

        // FIX 4: map idiomático no lugar do for loop
        const formattedProducts = edges.map(({ node }) => {
          const variants = node.variants?.edges?.map(({ node: variant }) => ({
            id: variant.id,
            title: variant.title,
            availableForSale: variant.availableForSale,
            price: parseFloat(variant.price.amount),
            compareAtPrice: variant.compareAtPrice?.amount
              ? parseFloat(variant.compareAtPrice.amount)
              : null
          })) || [];

          const availableVariants = variants.filter((variant) => variant.availableForSale);
          const variantsToUse = availableVariants.length > 0 ? availableVariants : variants;

          const cheapestVariant = variantsToUse.reduce((lowest, variant) => {
            if (!lowest || variant.price < lowest.price) return variant;
            return lowest;
          }, null);

          const discountedVariants = variantsToUse.filter(
            (variant) =>
              variant.compareAtPrice &&
              variant.compareAtPrice > variant.price
          );

          const cheapestDiscountedVariant = discountedVariants.reduce((lowest, variant) => {
            if (!lowest || variant.price < lowest.price) return variant;
            return lowest;
          }, null);

          const cheapestVariantHasDiscount =
            cheapestVariant?.compareAtPrice &&
            cheapestVariant.compareAtPrice > cheapestVariant.price;

          return {
            id: node.id,
            variantId: node.variants?.edges?.[0]?.node?.id ?? null,
            name: node.title,
            handle: node.handle,
            category: node.productType || 'Sem categoria',
            price: parseFloat(node.priceRange.minVariantPrice.amount),
            maxPrice: parseFloat(node.priceRange.maxVariantPrice.amount),
            variants,
            compareAtPrice: cheapestVariantHasDiscount
              ? cheapestVariant.compareAtPrice
              : null,
            hasVariantOnSale: discountedVariants.length > 0,
            saleVariantLabel: cheapestDiscountedVariant?.title || null,
            image_url: node.featuredImage?.url ?? '',
            stock: node.availableForSale ? 1 : 0,
            rating: null,
            review_count: null
          };
        });

        setFeaturedProducts(formattedProducts);
      } catch (error) {
        void error;
      }
    }

    fetchProducts();
  }, []);

  useEffect(() => {
    async function loadRatings() {
      if (!featuredProducts.length) return;

      const updatedProducts = await Promise.all(
        featuredProducts.map(async (product) => {
          if (product.rating !== null && product.review_count !== null) {
            return product;
          }

          const numericId = product.id.split('/').pop();
          const reviewStats = await fetchProductReviewStats(numericId, product.handle);

          return {
            ...product,
            rating: reviewStats ? reviewStats.average : null,
            review_count: reviewStats ? reviewStats.count : 0
          };
        })
      );

      setFeaturedProducts(updatedProducts);
    }

    const hasMissingRatings = featuredProducts.some(
      (product) => product.rating === null || product.review_count === null
    );

    if (hasMissingRatings) {
      loadRatings();
    }
  }, [featuredProducts]);

  function handleAddToCart(product) {
    if (!product.variantId) {
      toast.error('Esse produto não possui variante válida para checkout');
      return;
    }

    addItem(product);
    toast.success('Produto adicionado ao carrinho!');
  }

  // FIX 3: lê isInWishlist ANTES do toggle para exibir mensagem correta
  function handleToggleWishlist(product, e) {
    e.preventDefault();
    e.stopPropagation();
    const wasInWishlist = isInWishlist(product.id);
    toggleItem(product);
    toast.success(wasInWishlist ? 'Produto removido da wishlist' : 'Produto adicionado à wishlist');
  }

  return (
    <div className="min-h-screen">
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* FIX 5: imagem hero com fallback de cor caso a URL falhe */}
        <div
          className="absolute inset-0 z-0 bg-neutral-200"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1767552659473-9a541393de94)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-secondary"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6 max-w-4xl"
        >
          <motion.h1
            className="font-heading text-5xl md:text-7xl font-bold mb-6 text-text-primary tracking-tight flex items-center justify-center flex-wrap text-center gap-3 md:gap-5"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.span className="inline-block" variants={wordVariants}>
              Descubra
            </motion.span>
            <motion.span className="inline-block" variants={wordVariants}>
              sua
            </motion.span>
            <motion.span
              className="inline-block"
              initial={{ y: 80, opacity: 0 }}
              animate={auraEntrance}
            >
              <motion.span
                className="inline-block"
                initial={{ backgroundPosition: '200% 50%' }}
                animate={auraShimmer}
                style={{
                  background: 'linear-gradient(90deg, #9D5C63 0%, #c47c84 25%, #f0b8bc 50%, #c47c84 75%, #9D5C63 100%)',
                  backgroundSize: '300% 100%',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Aura
              </motion.span>
            </motion.span>
          </motion.h1>

          <p className="font-body text-lg md:text-xl text-text-secondary mb-8 leading-relaxed">
            Beleza e elegância para realçar sua essência única
          </p>

          <Link to="/produtos">
            <Button className="bg-accent text-white hover:bg-accent/90 rounded-full px-12 py-6 text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              Explorar Coleção <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl font-bold text-center mb-16 text-text-primary"
          >
            Categorias
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: 'Maquiagem', image: 'https://images.unsplash.com/photo-1631214499883-47ef353c0372', link: '/produtos?category=Maquiagem' },
              { name: 'Acessórios', image: 'https://images.unsplash.com/photo-1585409351049-f0d83b76c5dc', link: '/produtos?category=Acessórios' },
              { name: 'Skincare', image: 'https://images.unsplash.com/photo-1652464945507-687e44a1017a', link: '/produtos?category=Skincare' },
              { name: 'Tecnologia de Beleza', image: '/categoria-tecnologia.png', link: '/produtos?category=Tecnologia de Beleza' }
            ].map((category, idx) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="group relative overflow-hidden aspect-[3/4] cursor-pointer"
              >
                <Link to={category.link}>
                  <img
                    src={category.image}
                    alt={category.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
                    <h3 className="font-heading text-3xl font-bold text-white">{category.name}</h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-text-primary">
              Produtos em Destaque
            </h2>
            <p className="font-body text-lg text-text-secondary">
              Selecionados especialmente para você
            </p>
          </motion.div>

          {/* FIX 5: skeleton loader enquanto produtos carregam */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {featuredProducts.length === 0
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="group animate-pulse">
                    <div className="aspect-[3/4] bg-gray-100 mb-4 rounded" />
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-100 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                      <div className="h-6 bg-gray-100 rounded w-1/3" />
                    </div>
                  </div>
                ))
              : featuredProducts.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="group"
                  >
                    <Link to={`/produto/${encodeURIComponent(product.id)}`}>
                      <div className="relative aspect-[3/4] overflow-hidden mb-4 bg-gray-100">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />

                        {/* FIX 6: aria-label no botão de wishlist */}
                        <button
                          onClick={(e) => handleToggleWishlist(product, e)}
                          aria-label={
                            isInWishlist(product.id)
                              ? 'Remover da wishlist'
                              : 'Adicionar à wishlist'
                          }
                          className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 shadow-sm transition z-10"
                        >
                          <Heart
                            className={`w-5 h-5 ${
                              isInWishlist(product.id) ? 'fill-accent text-accent' : 'text-text-primary'
                            }`}
                          />
                        </button>

                        {product.stock === 0 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white font-semibold">Esgotado</span>
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="space-y-2">
                      <Link to={`/produto/${encodeURIComponent(product.id)}`}>
                        <h3 className="font-body font-semibold text-lg hover:text-accent transition-colors">
                          {product.name}
                        </h3>
                      </Link>

                      {product.review_count === null ? (
                        <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                      ) : product.review_count > 0 && product.rating !== null ? (
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 fill-accent text-accent" />
                            <span className="ml-1 text-sm text-text-secondary">
                              {product.rating.toFixed(1)}
                            </span>
                          </div>
                          <span className="text-sm text-text-muted">
                            ({product.review_count} avaliações)
                          </span>
                        </div>
                      ) : null}

                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          {product.compareAtPrice && product.compareAtPrice > product.price && (
                            <span className="text-sm text-text-muted line-through">
                              {product.compareAtPrice.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              })}
                            </span>
                          )}

                          <span className="font-heading text-2xl font-bold text-accent">
                            {product.maxPrice && product.maxPrice !== product.price
                              ? `A partir de ${product.price.toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                })}`
                              : product.price.toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                })}
                          </span>

                          {!product.compareAtPrice && product.hasVariantOnSale && (
                            <span className="mt-1 w-fit rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-accent">
                              Opção em oferta
                            </span>
                          )}
                        </div>

                        <Button
                          onClick={() => handleAddToCart(product)}
                          disabled={product.stock === 0 || !product.variantId}
                          aria-label={`Adicionar ${product.name} ao carrinho`}
                          className="bg-accent text-white hover:bg-accent/90 rounded-full p-3 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                        >
                          <ShoppingBag className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link to="/produtos">
              <Button
                variant="outline"
                className="rounded-full px-10 py-6 text-lg border-accent text-accent hover:bg-accent hover:text-white transition-all duration-300"
              >
                Ver Todos os Produtos
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
